// Admin > Overview tab — top-level stat tiles + the "who's using the app"
// role-mix breakdown. Presentational only; parent (AdminPage) is responsible
// for fetching `stats`.
import { Users, Mail, MessageSquare, BookOpen, Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { USER_TYPE_OPTIONS } from "@/lib/userTypes";

function StatTile({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="border-border shadow-none rounded-lg" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
            <p className="font-display text-3xl font-extrabold tracking-tight mt-1">{value ?? "—"}</p>
            {sub ? <p className="text-xs text-muted-foreground mt-1">{sub}</p> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BreakdownBar({ value, max, muted, testid }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 justify-end" data-testid={testid}>
      <div className="flex-1 max-w-[180px] h-1.5 bg-muted rounded overflow-hidden">
        <div
          className={`h-full rounded ${muted ? "bg-primary/60" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tabular-nums text-sm font-semibold w-10 text-right">{value.toLocaleString()}</span>
    </div>
  );
}

function UserTypesBreakdown({ stats }) {
  const total = stats?.user_types_total;
  const recent = stats?.user_types_recent;
  if (!total) return null;

  const answered = total.answered || 0;
  const unanswered = total.unanswered || 0;
  const recentTotal = stats?.users_recent_total || 0;
  const allTimeMax = Math.max(answered, 1, ...USER_TYPE_OPTIONS.map((o) => total[o.key] || 0));
  const recentMax = Math.max(recentTotal, 1, ...USER_TYPE_OPTIONS.map((o) => recent?.[o.key] || 0));

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="admin-user-types-breakdown">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Profile mix</p>
            <p className="font-display text-xl font-bold tracking-tight mt-0.5">Who&apos;s using Your Dog Days</p>
            <p className="text-xs text-muted-foreground mt-1">
              {answered.toLocaleString()} of {(answered + unanswered).toLocaleString()} users have answered &middot; {unanswered.toLocaleString()} haven&apos;t
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Last 7 days</p>
            <p className="font-display text-2xl font-extrabold tracking-tight mt-0.5" data-testid="admin-recent-users-count">{recentTotal.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">new signups</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm" data-testid="admin-user-types-table">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Role</th>
                <th className="text-right font-semibold px-4 py-2.5 w-[34%]">All-time</th>
                <th className="text-right font-semibold px-4 py-2.5 w-[34%]">Last 7 days</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {USER_TYPE_OPTIONS.map((opt) => {
                const allCount = total[opt.key] || 0;
                const recentCount = (recent && recent[opt.key]) || 0;
                return (
                  <tr key={opt.key} data-testid={`admin-user-types-row-${opt.key}`}>
                    <td className="px-4 py-2.5 font-medium">{opt.label}</td>
                    <td className="px-4 py-2.5">
                      <BreakdownBar value={allCount} max={allTimeMax} testid={`admin-bar-${opt.key}-all`} />
                    </td>
                    <td className="px-4 py-2.5">
                      <BreakdownBar value={recentCount} max={recentMax} muted testid={`admin-bar-${opt.key}-recent`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground mt-3">
          Users can pick more than one role, so percentages add to more than 100%. Pure analytics &mdash; no app behavior segments off these values.
        </p>
      </CardContent>
    </Card>
  );
}

export default function OverviewTab({ stats }) {
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile testid="stat-users" icon={Users} label="Users" value={stats?.users_total} sub={`${stats?.dogs_total ?? 0} dogs tracked`} />
        <StatTile testid="stat-subs" icon={Check} label="Active subs" value={stats?.subscriptions_active} />
        <StatTile testid="stat-newsletter" icon={Mail} label="Newsletter" value={stats?.newsletter_active} sub={`${stats?.newsletter_total ?? 0} total signups`} />
        <StatTile testid="stat-messages" icon={MessageSquare} label="Messages" value={stats?.contact_total} sub={`${stats?.contact_unread ?? 0} unread`} />
        <StatTile testid="stat-lessons" icon={BookOpen} label="Lessons" value={stats?.lessons_total} />
      </div>
      <UserTypesBreakdown stats={stats} />
    </div>
  );
}
